import cn from 'classnames';
import { forwardRef, useCallback } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';

import { Error } from 'components/UI/Tailwind/Error';
import { Label } from 'components/UI/Tailwind/Label';
import { ResizeableTextArea } from 'components/UI/Tailwind/ResizeableTextArea';

type TTextArea = JSX.IntrinsicElements['textarea'];

export interface FormTextArea
  extends Omit<TTextArea, 'name'>, Label, Pick<UseControllerProps, 'name'> {
  autoResize?: boolean;
  showCount?: boolean;
  showError?: boolean;
  variant?: 'inline' | 'primary';
}

export const FormTextArea = forwardRef<HTMLTextAreaElement, FormTextArea>(
  (
    {
      autoResize,
      children,
      className,
      label,
      labelSuffix,
      maxLength,
      name,
      onKeyDown,
      required,
      showCount,
      showError = true,
      variant = 'primary',
      ...props
    },
    ref,
  ) => {
    const { formState, register } = useFormContext();

    const value = useWatch({ name }) || '';

    const error = formState.errors?.[name]?.message ?? '';

    const field = register(name);

    const disabled = field.disabled || props.disabled || formState.isSubmitting;

    const setRef = useCallback(
      (node: HTMLTextAreaElement) => {
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            ref.current = node;
          }
        }
        field.ref(node);
      },
      [field, ref],
    );

    return (
      <div
        className={cn(
          autoResize
            ? {
                'border-gray-300 bg-gray-200 focus-within:border-gray-300 hover:border-gray-300':
                  disabled && variant === 'primary',
                'border-gray-400 focus-within:border-blue-500 focus-within:outline-blue-500':
                  !disabled && variant === 'primary',
                'border-red-600 focus-within:border-red-600 focus-within:outline-red-100':
                  showError && error && variant === 'primary',
                'cursor-not-allowed text-gray-500 placeholder:text-gray-500':
                  disabled,
                'cursor-text text-gray-800 placeholder:text-gray-500':
                  !disabled,
                'rounded-[8px] border px-3 py-2 text-14/140% outline-none focus-within:outline-1 focus-within:outline-offset-0':
                  variant === 'primary',
              }
            : {},
          className,
        )}
      >
        {label && (
          <Label
            label={label}
            labelSuffix={labelSuffix}
            required={required}
            variant={variant}
          />
        )}
        {autoResize ? (
          <ResizeableTextArea
            {...props}
            {...field}
            ref={setRef}
            autoFocus
            className={className}
            disabled={disabled}
            maxLength={maxLength}
            onBlur={(...args) => {
              field.onBlur(...args);
              props.onBlur?.(...args);
            }}
            onChange={(...args) => {
              field.onChange(...args);
              props.onChange?.(...args);
            }}
            onKeyDown={onKeyDown}
            rows={1}
            value={value}
          />
        ) : (
          <textarea
            {...props}
            {...field}
            ref={setRef}
            className={cn(
              'block h-[80px] max-h-[144px] min-h-[80px] w-full shadow-none outline-none transition duration-200',
              autoResize
                ? {}
                : {
                    'border-gray-300 bg-gray-200 hover:border-gray-300 focus:border-gray-300':
                      disabled && variant === 'primary',
                    'border-gray-400 hover:border-gray-400 focus:border-blue-500':
                      !disabled && variant === 'primary',
                    'border-red-600 focus:border-red-600 focus:outline-red-100':
                      showError && error && variant === 'primary',
                    'cursor-not-allowed text-gray-500 placeholder:text-gray-500':
                      disabled,
                    'cursor-text text-gray-800 placeholder:text-gray-500':
                      !disabled,
                    'rounded-[8px] border px-3 py-2 text-14/140% focus:outline-1 focus:outline-offset-0 focus:outline-blue-500':
                      variant === 'primary',
                  },
              className,
            )}
            disabled={disabled}
            maxLength={maxLength}
            onBlur={(...args) => {
              field.onBlur(...args);
              props.onBlur?.(...args);
            }}
            onChange={(...args) => {
              field.onChange(...args);
              props.onChange?.(...args);
            }}
            onKeyDown={onKeyDown}
            rows={1}
          />
        )}
        {showError && (
          // @ts-ignore
          <Error className={cn({ 'pb-2': !autoResize })}>{error}</Error>
        )}
        {(showCount || children) && (
          <div
            className={cn('flex justify-end gap-x-2 pt-2', {
              'col-start-1 row-start-2': autoResize,
            })}
          >
            {showCount && (
              <span className="text-10/100% font-normal text-gray-700">
                {value.length}/{maxLength}
              </span>
            )}
            {children}
          </div>
        )}
      </div>
    );
  },
);
